import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Context } from "../store/appContext";
import { confirmar } from "../utils/confirmar";
import { EMPRESAS } from "../utils/empresas";
import { apiFetch } from "../utils/apiFetch";
import { Button, Alert } from "../components/ui";

const toLocalYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const money = (n) => `${Number(n || 0).toFixed(2)} EUR`;
const INGRESO_CATEGORIAS = new Set([
  "ingreso_manual",
  "ingreso_cobro_inspeccion",
  "ingreso_cobro_profesional",
]);

const fechaMensual = (fechaBase, meses, dia) => {
  const year = fechaBase.getFullYear();
  const month = fechaBase.getMonth() + meses;
  return new Date(year, month, Math.min(Number(dia) || 1, new Date(year, month + 1, 0).getDate()), 12);
};

const fmtDate = (raw) => {
  if (!raw) return "-";
  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return String(raw).slice(0, 10);
  return toLocalYMD(dt);
};

// Compartidas entre el calculo del periodo filtrado y el saldo acumulado
// historico, para que ambos usen exactamente la misma logica y nunca
// diverjan entre si.
const calcularGastosAutoMaquinaria = (maquinariaResp, empresaFiltro, dDesde, dHasta) => {
  const maquinaria = Array.isArray(maquinariaResp) ? maquinariaResp : [];
  return maquinaria.flatMap((m) => {
    const rawFecha = m.fecha_compra || m.created_at;
    if (!rawFecha) return [];
    const compra = new Date(rawFecha);
    if (Number.isNaN(compra.getTime())) return [];
    const cantidad = Number(m.cantidad || 1);
    const baseConIva = Number(m.precio_con_iva || 0) > 0
      ? Number(m.precio_con_iva || 0)
      : Number(m.precio_sin_iva || 0) * (1 + Number(m.iva || 0) / 100);
    const total = Number(baseConIva || 0) * (cantidad > 0 ? cantidad : 1);
    if (total <= 0 || (empresaFiltro && empresaFiltro !== "sin_asignar" && m.empresa !== empresaFiltro) || (empresaFiltro === "sin_asignar" && m.empresa)) return [];
    const comun = {
      tipo: "gasto_auto_maquinaria",
      categoria: "compra_maquinaria_auto",
      proveedor: m.marca || "-",
      observaciones: m.modelo || "",
      origen: "maquinaria",
      empresa: m.empresa || null,
    };
    if (m.tratamiento_financiero !== "amortizable") {
      if (compra < dDesde || compra > dHasta) return [];
      return [{ ...comun, id: `auto-maq-${m.id}`, fecha: rawFecha, concepto: `Compra maquinaria: ${m.nombre || `#${m.id}`}`, importe: total }];
    }
    const meses = Number(m.vida_util_meses || 0), cuotas = [];
    for (let i = 0; i < meses; i += 1) {
      const fecha = fechaMensual(compra, i, compra.getDate());
      if (fecha > dHasta) break;
      if (fecha >= dDesde) cuotas.push({ ...comun, id: `auto-maq-${m.id}-${i}`, fecha: fecha.toISOString(), concepto: `Amortización maquinaria: ${m.nombre || `#${m.id}`}`, importe: total / meses });
    }
    return cuotas;
  });
};

const calcularGastosFijos = (fijosResp, empresaFiltro, dDesde, dHasta) => {
  const pasos = { mensual: 1, trimestral: 3, semestral: 6, anual: 12 };
  return (Array.isArray(fijosResp) ? fijosResp : []).flatMap((g) => {
    if (!g.activo || empresaFiltro === "sin_asignar") return [];
    const inicio = new Date(`${g.fecha_inicio}T12:00:00`);
    const fin = g.fecha_fin ? new Date(`${g.fecha_fin}T23:59:59`) : dHasta;
    const paso = pasos[g.periodicidad] || 1;
    const out = [];
    let i = 0, cursor = fechaMensual(inicio, i, g.dia_vencimiento);
    while (cursor < inicio) { i += paso; cursor = fechaMensual(inicio, i, g.dia_vencimiento); }
    while (cursor <= dHasta && cursor <= fin) {
      if (cursor >= dDesde) out.push({ ...g, id: `fijo-${g.id}-${cursor.toISOString().slice(0, 10)}`, fecha: cursor.toISOString(), origen: "gasto_fijo" });
      i += paso;
      cursor = fechaMensual(inicio, i, g.dia_vencimiento);
    }
    return out;
  });
};

// El ingreso de un cobro de inspeccion guarda "tipo=profesional" o
// "tipo=particular" en observaciones (ver _aplicar_cobro_inspeccion en el
// backend). Los ingresos manuales no llevan esa marca y se cuentan como
// particulares por defecto.
const esIngresoConcesionario = (g) => /tipo=profesional/i.test(String(g?.observaciones || ""));

export default function GastosEmpresaPage() {
  const { actions } = useContext(Context);
  const navigate = useNavigate();

  const darkPanel = {
    background: "var(--sw-surface)",
    border: "1px solid var(--sw-border)",
    color: "var(--sw-text)",
  };

  const darkInputStyle = {
    background: "var(--sw-surface-2)",
    border: "1px solid var(--sw-border)",
    color: "var(--sw-text)",
  };

  const darkInputClass = "form-control";
  const darkSelectClass = "form-select";

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [origenesVisibles, setOrigenesVisibles] = useState({
    manual: true,
    gasto_fijo: true,
    maquinaria: true,
    entrada: true,
  });
  const toggleOrigen = (key) => setOrigenesVisibles((prev) => ({ ...prev, [key]: !prev[key] }));

  const [mesSeleccionado, setMesSeleccionado] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [categoria, setCategoria] = useState("");
  const [q, setQ] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [tipoCliente, setTipoCliente] = useState("");

  const [gastos, setGastos] = useState([]);
  const [ingresosAutomaticos, setIngresosAutomaticos] = useState(0);
  const [gastosAutoEntradas, setGastosAutoEntradas] = useState([]);
  const [gastosAutoMaquinaria, setGastosAutoMaquinaria] = useState([]);
  const [gastosFijos, setGastosFijos] = useState([]);
  const [cobrosProfesionales, setCobrosProfesionales] = useState([]);
  const [saldoAcumulado, setSaldoAcumulado] = useState(null);
  const [ingresosAcumulados, setIngresosAcumulados] = useState(0);
  const [gastosAcumuladosTotal, setGastosAcumuladosTotal] = useState(0);

  useEffect(() => {
    const [year, month] = mesSeleccionado.split("-").map(Number);
    const first = new Date(year, month - 1, 1);
    const last = new Date(year, month, 0);
    setDesde(toLocalYMD(first));
    setHasta(toLocalYMD(last));
  }, [mesSeleccionado]);

  const cargarDatos = async (nextDesde = desde, nextHasta = hasta, nextCategoria = categoria, nextQ = q) => {
    if (!nextDesde || !nextHasta) return;

    setLoading(true);
    try {
      // Los coches/inspecciones no tienen campo empresa todavia (solo se usan
      // para SWAutospa por ahora), asi que este filtro no lleva `empresa`.
      const tipoClienteQS = tipoCliente === "pendiente"
        ? "&solo_pendiente=1"
        : (tipoCliente ? `&tipo_cliente=${tipoCliente}` : "");

      const [gastosResp, facturacionPeriodo, entradasResp, maquinariaResp, fijosResp] = await Promise.all([
        actions.getGastosEmpresa({ desde: nextDesde, hasta: nextHasta, categoria: nextCategoria, empresa, q: nextQ }),
        // Facturacion (devengo) de coches entregados en el periodo, aunque no
        // se hayan cobrado todavia. Antes leia de Cliente/Coche/Servicio
        // (getReporteClientes), una tabla legacy sin datos desde que el flujo
        // pasa por InspeccionRecepcion; por eso siempre daba 0.
        apiFetch(`/api/reportes/facturacion-periodo?desde=${nextDesde}&hasta=${nextHasta}${tipoClienteQS}`),
        actions.getEntradas({ desde: nextDesde, hasta: nextHasta }),
        actions.getMaquinaria(),
        apiFetch(`/api/gastos-fijos${empresa && empresa !== "sin_asignar" ? `?empresa=${empresa}` : ""}`),
      ]);

      const cobrosProfResp = await actions.getCobrosProfesionales({
        soloPendientes: false,
        desde: nextDesde,
        hasta: nextHasta,
      });
      setCobrosProfesionales(Array.isArray(cobrosProfResp) ? cobrosProfResp : []);

      const items = Array.isArray(gastosResp?.items) ? gastosResp.items : [];
      setGastos(items);

      setIngresosAutomaticos(Number(facturacionPeriodo?.total || 0));

      const entradas = Array.isArray(entradasResp) ? entradasResp : [];
      const autosEntradas = entradas
        .filter((e) => Number(e?.precio_con_iva || 0) > 0)
        .map((e) => ({
          id: `auto-ent-${e.id}`,
          tipo: "gasto_auto_producto",
          fecha: e.fecha || e.created_at,
          concepto: `Compra producto: ${e.producto_nombre || `#${e.producto_id}`}`,
          categoria: "compra_producto_auto",
          proveedor: e.proveedor_nombre || "-",
          observaciones: e.numero_albaran || e.numero_documento || "",
          importe: Number(e.precio_con_iva || 0),
          origen: "entrada",
        }));
      setGastosAutoEntradas(autosEntradas);

      const dDesde = new Date(`${nextDesde}T00:00:00`);
      const dHasta = new Date(`${nextHasta}T23:59:59`);
      setGastosAutoMaquinaria(calcularGastosAutoMaquinaria(maquinariaResp, empresa, dDesde, dHasta));
      setGastosFijos(calcularGastosFijos(fijosResp, empresa, dDesde, dHasta));
    } catch (err) {
      console.error("cargarDatos finanzas:", err);
      setGastos([]);
      setIngresosAutomaticos(0);
      setGastosAutoEntradas([]);
      setGastosAutoMaquinaria([]);
      setGastosFijos([]);
      setCobrosProfesionales([]);
    } finally {
      setLoading(false);
    }
  };

  const calcularSaldoAcumulado = async () => {
    // Balance de caja historico, desde el inicio hasta hoy, sin importar el
    // filtro de fecha de arriba. Lee ingresos y gastos DIRECTAMENTE de las
    // hojas de Google Sheets (coches para ingresos, gastos fijos empresa
    // para gastos) en vez de reconstruirlos a partir de los registros del
    // sistema: verificamos que el sistema tiene ~430 coches sin registrar y
    // gastos duplicados entre lo importado y los gastos fijos recurrentes,
    // mientras que las hojas de Sheets son la fuente que la familia lleva al
    // dia y coincide con una verificacion independiente.
    const desdeInicio = "2026-01-01";
    const hoy = toLocalYMD(new Date());
    try {
      const saldoResp = await apiFetch(`/api/reportes/saldo-real-sheets?desde=${desdeInicio}&hasta=${hoy}&empresa=${empresa || "autospa"}`);

      setIngresosAcumulados(Number(saldoResp?.ingresos?.total || 0));
      setGastosAcumuladosTotal(Number(saldoResp?.gastos?.total || 0));
      setSaldoAcumulado(Number(saldoResp?.resultado ?? 0));
    } catch (err) {
      console.error("calcularSaldoAcumulado:", err);
      setSaldoAcumulado(null);
      setIngresosAcumulados(0);
      setGastosAcumuladosTotal(0);
    }
  };

  useEffect(() => {
    if (desde && hasta) {
      cargarDatos(desde, hasta, categoria, q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desde, hasta, tipoCliente]);

  useEffect(() => {
    calcularSaldoAcumulado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresa]);

  const totales = useMemo(() => {
    const ingresosItems = gastos.filter((g) => {
      const cat = String(g.categoria || "").toLowerCase();
      if (!INGRESO_CATEGORIAS.has(cat)) return false;
      if (tipoCliente === "profesional") return esIngresoConcesionario(g);
      if (tipoCliente === "particular") return !esIngresoConcesionario(g);
      return true;
    });

    const ingresosCobrados = ingresosItems.reduce((acc, g) => acc + Number(g.importe || 0), 0);

    // De los ingresos con entrega en el periodo filtrado, cuanto se cobro
    // (created_at) dentro del propio periodo vs fuera de el (pago adelantado
    // o atrasado respecto a la entrega).
    const dDesdeCobro = desde ? new Date(`${desde}T00:00:00`) : null;
    const dHastaCobro = hasta ? new Date(`${hasta}T23:59:59`) : null;
    const ingresosCobradosEnPeriodo = ingresosItems
      .filter((g) => {
        if (!dDesdeCobro || !dHastaCobro) return true;
        const creado = new Date(g.created_at || g.fecha);
        if (Number.isNaN(creado.getTime())) return true;
        return creado >= dDesdeCobro && creado <= dHastaCobro;
      })
      .reduce((acc, g) => acc + Number(g.importe || 0), 0);
    const ingresosCobradosFueraPeriodo = ingresosCobrados - ingresosCobradosEnPeriodo;

    const totalGastosManuales = gastos
      .filter((g) => {
        const cat = String(g.categoria || "").toLowerCase();
        return !INGRESO_CATEGORIAS.has(cat);
      })
      .reduce((acc, g) => acc + Number(g.importe || 0), 0);

    const totalGastosEntradasAuto = gastosAutoEntradas.reduce((acc, g) => acc + Number(g.importe || 0), 0);
    const totalGastosMaquinariaAuto = gastosAutoMaquinaria.reduce((acc, g) => acc + Number(g.importe || 0), 0);
    const totalGastosFijos = gastosFijos.reduce((acc, g) => acc + Number(g.importe || 0), 0);
    const totalGastos = totalGastosManuales + totalGastosEntradasAuto + totalGastosMaquinariaAuto + totalGastosFijos;

    const ingresosFacturados = Number(ingresosAutomaticos || 0);
    const balanceCaja = ingresosCobrados - totalGastos;
    const balanceDevengado = ingresosFacturados - totalGastos;
    const pendienteProfesionales = (cobrosProfesionales || []).reduce(
      (acc, c) => acc + Number(c.total_pendiente || 0),
      0
    );
    const facturacionProfesionales = (cobrosProfesionales || []).reduce(
      (acc, c) => acc + Number(c.total_facturado || 0),
      0
    );

    return {
      ingresosCobrados,
      ingresosCobradosEnPeriodo,
      ingresosCobradosFueraPeriodo,
      ingresosFacturados,
      totalGastosManuales,
      totalGastosEntradasAuto,
      totalGastosMaquinariaAuto,
      totalGastosFijos,
      totalGastos,
      balanceCaja,
      balanceDevengado,
      pendienteProfesionales,
      facturacionProfesionales,
    };
  }, [gastos, gastosAutoEntradas, gastosAutoMaquinaria, gastosFijos, ingresosAutomaticos, cobrosProfesionales, desde, hasta, tipoCliente]);

  const movimientosTabla = useMemo(() => {
    const manual = (gastos || []).map((g) => ({ ...g, origen: "manual" }));
    const merged = [...manual, ...gastosAutoEntradas, ...gastosAutoMaquinaria, ...gastosFijos];
    return merged.sort((a, b) => {
      const da = new Date(a.fecha || a.created_at || 0).getTime();
      const db = new Date(b.fecha || b.created_at || 0).getTime();
      return db - da;
    });
  }, [gastos, gastosAutoEntradas, gastosAutoMaquinaria, gastosFijos]);

  const movimientosFiltrados = useMemo(() => {
    // Los manuales ya vienen filtrados por categoria/q desde el backend
    // (cargarDatos). Los automaticos (fijos, maquinaria, productos) se
    // calculan aparte sin pasar por ese filtro, asi que hay que aplicarselo
    // aqui o siempre saldrian todos sin importar lo que se busque/filtre.
    const catFiltro = String(categoria || "").trim().toLowerCase();
    const qFiltro = String(q || "").trim().toLowerCase();
    return movimientosTabla.filter((m) => {
      const origen = String(m.origen || "");
      const isManual = origen === "manual";
      if (!origenesVisibles[origen]) return false;

      const cat = String(m.categoria || "").toLowerCase();
      if (INGRESO_CATEGORIAS.has(cat) && tipoCliente) {
        if (tipoCliente === "profesional" && !esIngresoConcesionario(m)) return false;
        if (tipoCliente === "particular" && esIngresoConcesionario(m)) return false;
      }

      if (!isManual) {
        if (catFiltro && cat !== catFiltro) return false;
        if (qFiltro) {
          const haystack = `${m.concepto || ""} ${m.proveedor || ""} ${m.observaciones || ""}`.toLowerCase();
          if (!haystack.includes(qFiltro)) return false;
        }
      }
      return true;
    });
  }, [movimientosTabla, origenesVisibles, tipoCliente, categoria, q]);

  const exportarCSV = () => {
    const rows = movimientosFiltrados.map((g) => {
      const cat = String(g.categoria || "").toLowerCase();
      const isIngreso = INGRESO_CATEGORIAS.has(cat);
      const tipo = isIngreso ? "Ingreso" : "Gasto";
      const origen = String(g.origen || "") || "manual";
      const importeSigned = isIngreso
        ? Number(g.importe || 0)
        : -Number(g.importe || 0);

      return [
        fmtDate(g.fecha || g.created_at),
        tipo,
        origen,
        g.concepto || "",
        g.categoria || "",
        g.proveedor || "",
        g.observaciones || "",
        String(importeSigned.toFixed(2)),
      ];
    });

    const header = ["Fecha", "Tipo", "Origen", "Concepto", "Categoria", "Proveedor", "Observaciones", "Importe"];
    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";"))
      .join("\n");

    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `finanzas_${desde || "desde"}_${hasta || "hasta"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportarPDF = () => {
    window.print();
  };

  const eliminarGasto = async (id) => {
    if (!await confirmar("¿Deseas eliminar este gasto?")) return;
    try {
      await actions.deleteGastoEmpresa(id);
      await cargarDatos();
    } catch (err) {
      setErrorMsg(`No se pudo eliminar: ${err?.message || "error"}`);
    }
  };

  return (
    <div className="container py-4 sw-page-shell sw-view-stack" style={{ color: "var(--sw-text)" }}>
      <div className="d-flex justify-content-between align-items-center mb-3 sw-view-header">
        <h2 className="mb-0" style={{ color: "var(--sw-text)" }}>Finanzas Empresa</h2>
        <div className="d-flex gap-2">
          <Button variant="primary" onClick={() => navigate("/administracion/registrar-gasto")}>
            + Registrar gasto
          </Button>
          <Button variant="outline" onClick={exportarCSV} disabled={loading || !movimientosFiltrados.length}>
            Exportar Excel (CSV)
          </Button>
          <Button variant="outline" onClick={exportarPDF} disabled={loading || !movimientosFiltrados.length}>
            Exportar PDF
          </Button>
          <Button variant="outline" onClick={() => cargarDatos()} loading={loading}>
            Actualizar
          </Button>
        </div>
      </div>

      {errorMsg && (
        <Alert variant="danger" onClose={() => setErrorMsg("")}>
          {errorMsg}
        </Alert>
      )}

      <div className="d-flex gap-3 mb-3 flex-wrap">
        {[
          { key: "manual", label: "Manuales" },
          { key: "gasto_fijo", label: "Gastos fijos" },
          { key: "maquinaria", label: "Maquinaria" },
          { key: "entrada", label: "Productos" },
        ].map(({ key, label }) => (
          <div className="form-check" key={key}>
            <input
              id={`mov-origen-${key}`}
              type="checkbox"
              className="form-check-input"
              checked={origenesVisibles[key]}
              onChange={() => toggleOrigen(key)}
            />
            <label className="form-check-label" htmlFor={`mov-origen-${key}`} style={{ color: "var(--sw-text)" }}>
              {label}
            </label>
          </div>
        ))}
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <label className="form-label fw-semibold" style={{ color: "var(--sw-text)" }}>Empresa</label>
          <select className={darkSelectClass} style={darkInputStyle} value={empresa} onChange={(e) => setEmpresa(e.target.value)}>
            <option value="">Grupo SW</option>
            {EMPRESAS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            <option value="sin_asignar">Sin asignar</option>
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label fw-semibold" style={{ color: "var(--sw-text)" }}>Mes</label>
          <input
            type="month"
            className={darkInputClass}
            style={darkInputStyle}
            value={mesSeleccionado}
            onChange={(e) => setMesSeleccionado(e.target.value)}
          />
        </div>
        <div className="col-md-2">
          <label className="form-label fw-semibold" style={{ color: "var(--sw-text)" }}>Desde</label>
          <input
            type="date"
            className={darkInputClass}
            style={darkInputStyle}
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
          />
        </div>
        <div className="col-md-2">
          <label className="form-label fw-semibold" style={{ color: "var(--sw-text)" }}>Hasta</label>
          <input
            type="date"
            className={darkInputClass}
            style={darkInputStyle}
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
          />
        </div>
        <div className="col-md-2">
          <label className="form-label fw-semibold" style={{ color: "var(--sw-text)" }}>Categoria</label>
          <input
            type="text"
            className={darkInputClass}
            style={darkInputStyle}
            placeholder="general, nomina..."
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
          />
        </div>
        <div className="col-md-2">
          <label className="form-label fw-semibold" style={{ color: "var(--sw-text)" }}>Entrada</label>
          <select className={darkSelectClass} style={darkInputStyle} value={tipoCliente} onChange={(e) => setTipoCliente(e.target.value)}>
            <option value="">Todos los clientes</option>
            <option value="particular">Particulares</option>
            <option value="profesional">Concesionarios</option>
            <option value="pendiente">Falta cobrar</option>
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label fw-semibold" style={{ color: "var(--sw-text)" }}>Buscar</label>
          <div className="d-flex gap-2">
            <input
              type="text"
              className={darkInputClass}
              style={darkInputStyle}
              placeholder="concepto, proveedor..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <button
              className="btn btn-dark"
              type="button"
              onClick={() => cargarDatos(desde, hasta, categoria, q)}
            >
              Filtrar
            </button>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card border-success h-100" style={darkPanel}>
            <div className="card-body">
              <div className="small" style={{ color: "var(--sw-muted)" }}>Facturación del periodo (devengo)</div>
              <div className="fs-4 fw-bold text-success">{money(ingresosAutomaticos)}</div>
              <div className="small" style={{ color: "var(--sw-muted)" }}>
                {tipoCliente === "pendiente" && "Solo coches con algo pendiente de cobrar"}
                {tipoCliente === "particular" && "Solo clientes particulares"}
                {tipoCliente === "profesional" && "Solo concesionarios"}
                {!tipoCliente && "Coches entregados en el periodo, se hayan cobrado o no"}
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-info h-100" style={darkPanel}>
            <div className="card-body">
              <div className="small" style={{ color: "var(--sw-muted)" }}>Ingresos cobrados (caja real)</div>
              <div className="fs-4 fw-bold text-info">{money(totales.ingresosCobrados)}</div>
              <div className="small" style={{ color: "var(--sw-muted)" }}>
                De coches entregados en el periodo. Cobrado dentro del periodo {money(totales.ingresosCobradosEnPeriodo)}
                {Math.abs(totales.ingresosCobradosFueraPeriodo) > 0.01 && (
                  <> · fuera del periodo (adelantado/atrasado) {money(totales.ingresosCobradosFueraPeriodo)}</>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-success h-100" style={darkPanel}>
            <div className="card-body">
              <div className="small" style={{ color: "var(--sw-muted)" }}>Facturación concesionarios (IVA incluido)</div>
              <div className="fs-4 fw-bold text-success">{money(totales.facturacionProfesionales)}</div>
              <div className="small" style={{ color: "var(--sw-muted)" }}>
                Pendiente de cobro: {money(totales.pendienteProfesionales)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <div className={`card h-100 ${totales.balanceCaja >= 0 ? "border-primary" : "border-warning"}`} style={darkPanel}>
            <div className="card-body">
              <div className="small" style={{ color: "var(--sw-muted)" }}>Resultado caja (cobrado - gastos)</div>
              <div className={`fs-4 fw-bold ${totales.balanceCaja >= 0 ? "text-primary" : "text-warning"}`}>
                {money(totales.balanceCaja)}
              </div>
              <div className="small" style={{ color: "var(--sw-muted)" }}>
                Cobrado {money(totales.ingresosCobrados)} - Gastos {money(totales.totalGastos)}
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card border-danger h-100" style={darkPanel}>
            <div className="card-body">
              <div className="small" style={{ color: "var(--sw-muted)" }}>Gastos del periodo (total)</div>
              <div className="fs-5 fw-bold text-danger">{money(totales.totalGastos)}</div>
              <div className="small" style={{ color: "var(--sw-muted)" }}>
                Manuales {money(totales.totalGastosManuales)} | Productos auto {money(totales.totalGastosEntradasAuto)} | Maquinaria auto {money(totales.totalGastosMaquinariaAuto)} | Fijos {money(totales.totalGastosFijos)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-12">
          {(() => {
            const esPositivo = saldoAcumulado === null || saldoAcumulado >= 0;
            return (
              <div className={`card h-100 ${esPositivo ? "border-primary" : "border-warning"}`} style={darkPanel}>
                <div className="card-body">
                  <div className="small" style={{ color: "var(--sw-muted)" }}>Saldo real (según hojas de Sheets: ingresos − gastos, 2026 hasta hoy)</div>
                  <div className={`fs-4 fw-bold ${esPositivo ? "text-primary" : "text-warning"}`}>
                    {saldoAcumulado === null ? "Calculando..." : money(saldoAcumulado)}
                  </div>
                  <div className="small" style={{ color: "var(--sw-muted)" }}>
                    Ingresos {money(ingresosAcumulados)} − Gastos {money(gastosAcumuladosTotal)}. Sin importar el filtro de arriba.
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      <div className="card" style={darkPanel}>
        <div className="card-header d-flex justify-content-between align-items-center" style={{ background: "var(--sw-surface-light)", color: "var(--sw-text)", borderBottom: "1px solid var(--sw-border)" }}>
          <span className="fw-semibold">Movimientos (manuales + automaticos)</span>
          <span className="badge" style={{ background: "var(--sw-accent)", color: "var(--sw-text-on-accent)" }}>{movimientosFiltrados.length} items</span>
        </div>
        <div className="table-responsive">
          <table
            className="table table-sm table-striped mb-0"
style={{
              color: "var(--sw-text)",
            }}
          >
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Tipo</th>
                <th>Origen</th>
                <th>Concepto</th>
                <th>Categoria</th>
                <th>Proveedor</th>
                <th>Observaciones</th>
                <th className="text-end">Importe</th>
                <th className="text-end">Accion</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={9} className="text-center py-3">Cargando...</td>
                </tr>
              )}
              {!loading && movimientosFiltrados.map((g) => (
                <tr key={g.id}>
                  <td>{fmtDate(g.fecha || g.created_at)}</td>
                  <td>
                    {(() => {
                      const cat = String(g.categoria || "").toLowerCase();
                      if (cat === "ingreso_manual") {
                        return <span className="badge bg-success">Ingreso manual</span>;
                      }
                      if (cat === "ingreso_cobro_inspeccion" || cat === "ingreso_cobro_profesional") {
                        return <span className="badge bg-success">Cobro inspección</span>;
                      }
                      if (String(g.origen || "") === "entrada") {
                        return <span className="badge bg-danger">Gasto auto</span>;
                      }
                      if (String(g.origen || "") === "maquinaria") {
                        return <span className="badge bg-danger">Gasto auto</span>;
                      }
                      return <span className="badge bg-danger">Gasto</span>;
                    })()}
                  </td>
                  <td>
                    {String(g.origen || "") === "entrada" && <span className="badge bg-secondary">Productos</span>}
                    {String(g.origen || "") === "maquinaria" && <span className="badge bg-secondary">Maquinaria</span>}
                    {String(g.origen || "") === "manual" && (
                      <span className="badge" style={{ background: "#495057", color: "#fff" }}>Manual</span>
                    )}
                  </td>
                  <td>{g.concepto}</td>
                  <td style={{ textTransform: "capitalize" }}>{g.categoria || "-"}</td>
                  <td>{g.proveedor || "-"}</td>
                  <td>{g.observaciones || "-"}</td>
                  <td className="text-end fw-semibold">
                    {(() => {
                      const cat = String(g.categoria || "").toLowerCase();
                      return INGRESO_CATEGORIAS.has(cat) ? "+" : "-";
                    })()}
                    {money(g.importe)}
                  </td>
                  <td className="text-end">
                    {String(g.origen || "") === "manual" ? (
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => eliminarGasto(g.id)}
                      >
                        Eliminar
                      </button>
                    ) : (
                      <span className="small" style={{ color: "#cbd3da" }}>Automatico</span>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && !movimientosFiltrados.length && (
                <tr>
                  <td colSpan={9} className="text-center py-4" style={{ color: "#cbd3da" }}>
                    No hay movimientos para los filtros seleccionados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
